import { createAuditEvent } from "../db/audit";
import type { CEOOSDatabase } from "../db/database";
import { createEntityMetadata } from "../domain/common";
import { dateKeyForInstant } from "../domain/business-date";
import {
  createEvidenceStatement,
  createFocusDeviation,
  createIdeaParkingItem,
  createNoStartItem,
  createPromiseEntry,
} from "../domain/factories";
import type {
  DailyCEOReview,
  EvidenceStatement,
  EvidenceStatementType,
  FocusDeviationRecord,
  FocusEscalationRecord,
  IdeaParkingItem,
  NoStartItem,
  PromiseEntry,
  StructuredCEORecord,
} from "../domain/types";
import {
  projectGovernanceRisk,
  type GovernanceRiskProjection,
} from "../privacy/governance-projection";
import {
  coolingUntilFromHours,
  evaluateFocusActivation,
  type FocusActivationAnswers,
  type FocusActivationDecision,
} from "../rules/focus-activation";
import {
  cancelPromise,
  evaluatePromiseDueStatus,
  fulfillPromise,
  markPromiseBreached,
  promiseAttentionRank,
  renegotiatePromise,
  requestPromiseRenegotiation,
  type PromiseRuleIssue,
} from "../rules/promise-rules";
import { auditTruthStatements, type TruthAuditIssue } from "../rules/truth-audit";
import { saveStructuredRecord } from "../db/repositories";
import type { BusinessContextView } from "./round02-service";

export interface TruthOverview {
  statements: EvidenceStatement[];
  inbox: EvidenceStatement[];
  issues: TruthAuditIssue[];
  topRisk?: EvidenceStatement | undefined;
}

export interface FocusOverview {
  ideas: IdeaParkingItem[];
  noStartItems: NoStartItem[];
  deviations: FocusDeviationRecord[];
  parkedCount: number;
  topRisk?: FocusDeviationRecord | IdeaParkingItem | undefined;
}

export interface PromiseOverview {
  promises: PromiseEntry[];
  topRisk?: PromiseEntry | undefined;
  todayDue: PromiseEntry[];
  dueSoon: PromiseEntry[];
  overdue: PromiseEntry[];
  renegotiationNeeded: PromiseEntry[];
  fulfilled: PromiseEntry[];
  breached: PromiseEntry[];
}

export interface GovernanceOverview {
  truth: TruthOverview;
  focus: FocusOverview;
  promise: PromiseOverview;
  projection: GovernanceRiskProjection;
}

function isEvidenceStatement(record: StructuredCEORecord): record is EvidenceStatement {
  return record.recordType === "evidence_statement";
}

function isIdea(record: StructuredCEORecord): record is IdeaParkingItem {
  return record.recordType === "idea_parking";
}

function isNoStart(record: StructuredCEORecord): record is NoStartItem {
  return record.recordType === "no_start";
}

function isFocusDeviation(record: StructuredCEORecord): record is FocusDeviationRecord {
  return record.recordType === "focus_deviation";
}

function isPromise(record: StructuredCEORecord): record is PromiseEntry {
  return record.recordType === "promise";
}

function activeRecord(record: StructuredCEORecord): boolean {
  return record.deletionState === "active";
}

function shiftIsoDays(now: string, days: number): string {
  return new Date(new Date(now).getTime() + days * 24 * 60 * 60 * 1000).toISOString();
}

async function updateOverduePromises(
  database: CEOOSDatabase,
  promises: readonly PromiseEntry[],
  now: string,
): Promise<PromiseEntry[]> {
  const next: PromiseEntry[] = [];
  for (const promise of promises) {
    const evaluated = evaluatePromiseDueStatus(promise, now);
    next.push(evaluated.promise);
    if (evaluated.promise !== promise) {
      await database.transaction(
        "rw",
        database.structuredRecords,
        database.auditEvents,
        async () => {
          await database.structuredRecords.put(evaluated.promise);
          await database.auditEvents.put(
            createAuditEvent({
              entityType: "promise",
              entityId: promise.id,
              action: "update",
              actor: "system",
              reason: "确定性到期规则把 active 承诺标记为 overdue；未标记为 breached。",
              changedFields: ["status", "promiseStatus", "history"],
              now,
            }),
          );
        },
      );
    }
  }
  return next;
}

export async function loadGovernanceOverview(
  database: CEOOSDatabase,
  dateKey: string,
  now = new Date().toISOString(),
): Promise<GovernanceOverview> {
  const records = (await database.structuredRecords.toArray()).filter(activeRecord);
  const statements = records.filter(isEvidenceStatement);
  const truthIssues = auditTruthStatements(statements, now);
  const topTruthIssue = truthIssues[0];
  const topTruthRisk = topTruthIssue
    ? statements.find((statement) => topTruthIssue.statementIds.includes(statement.id))
    : statements
        .filter((statement) => statement.truthInboxStatus === "inbox")
        .sort((left, right) => left.createdAt.localeCompare(right.createdAt))[0];

  const ideas = records
    .filter(isIdea)
    .sort((left, right) => right.capturedAt.localeCompare(left.capturedAt));
  const noStartItems = records.filter(isNoStart);
  const deviations = records
    .filter(isFocusDeviation)
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
  const topFocusRisk =
    deviations.find((deviation) => deviation.affectedCriticalBusinessAction) ??
    ideas.find(
      (idea) =>
        idea.parkingStatus === "under_review" || idea.parkingStatus === "escalated_to_board",
    );

  const promises = await updateOverduePromises(database, records.filter(isPromise), now);
  const openPromises = promises
    .filter((promise) => promise.promiseStatus !== "archived")
    .sort(
      (left, right) =>
        promiseAttentionRank(right, now) - promiseAttentionRank(left, now) ||
        left.dueAt.localeCompare(right.dueAt),
    );
  const nowMs = new Date(now).getTime();
  const dueSoonLimit = nowMs + 7 * 24 * 60 * 60 * 1000;
  const promiseOverview: PromiseOverview = {
    promises: openPromises,
    topRisk: openPromises.find((promise) => promiseAttentionRank(promise, now) >= 0),
    todayDue: openPromises.filter(
      (promise) =>
        dateKeyForInstant(promise.dueAt, promise.businessTimezone) === dateKey &&
        new Date(promise.dueAt).getTime() >= nowMs &&
        promise.promiseStatus !== "fulfilled",
    ),
    dueSoon: openPromises.filter((promise) => {
      const due = new Date(promise.dueAt).getTime();
      return due >= nowMs && due <= dueSoonLimit && promise.promiseStatus !== "fulfilled";
    }),
    overdue: openPromises.filter(
      (promise) =>
        promise.promiseStatus === "overdue" ||
        (new Date(promise.dueAt).getTime() < nowMs &&
          promise.promiseStatus !== "fulfilled" &&
          promise.promiseStatus !== "cancelled"),
    ),
    renegotiationNeeded: openPromises.filter(
      (promise) => promise.promiseStatus === "renegotiation_needed",
    ),
    fulfilled: openPromises.filter((promise) => promise.promiseStatus === "fulfilled"),
    breached: openPromises.filter((promise) => promise.promiseStatus === "breached"),
  };

  const focusOverview: FocusOverview = {
    ideas,
    noStartItems,
    deviations,
    parkedCount: ideas.filter((idea) => idea.parkingStatus === "parked").length,
    topRisk: topFocusRisk,
  };
  const truthOverview: TruthOverview = {
    statements,
    inbox: statements.filter((statement) => statement.truthInboxStatus === "inbox"),
    issues: truthIssues,
    topRisk: topTruthRisk,
  };
  return {
    truth: truthOverview,
    focus: focusOverview,
    promise: promiseOverview,
    projection: projectGovernanceRisk(
      {
        statements,
        blockingTruthIssueCount: truthIssues.filter((item) => item.severity === "blocking").length,
        deviations,
        ideas,
        promises,
      },
      now,
    ),
  };
}

export async function addTruthStatement(
  database: CEOOSDatabase,
  input: {
    type: EvidenceStatementType;
    content: string;
    effectiveDate: string;
    sensitivity?: EvidenceStatement["sensitivity"];
  },
  now = new Date().toISOString(),
): Promise<EvidenceStatement> {
  const statement = createEvidenceStatement(
    {
      type: input.type,
      content: input.content.trim(),
      effectiveDate: input.effectiveDate,
      generatedBy: "user",
      sensitivity: input.sensitivity ?? "standard",
      truthInboxStatus: "inbox",
      reviewDueAt: shiftIsoDays(now, 30),
    },
    now,
  );
  await saveStructuredRecord(database, statement);
  return statement;
}

export async function saveReviewStatementToTruthInbox(
  database: CEOOSDatabase,
  review: DailyCEOReview,
  sourceStatementId: string,
  targetType: EvidenceStatementType,
  now = new Date().toISOString(),
): Promise<EvidenceStatement> {
  const source = await database.structuredRecords.get(sourceStatementId);
  if (!source || !isEvidenceStatement(source) || !review.statementIds.includes(source.id)) {
    throw new Error("CEO-TRUTH-101: 找不到可追溯的复盘陈述。");
  }
  if (source.statementType === targetType) {
    const updated: EvidenceStatement = {
      ...source,
      truthInboxStatus: "inbox",
      reviewDueAt: source.reviewDueAt ?? shiftIsoDays(now, 30),
      updatedAt: now,
    };
    await saveStructuredRecord(
      database,
      updated,
      createAuditEvent({
        entityType: "evidence_statement",
        entityId: updated.id,
        action: "update",
        actor: "user",
        reason: "用户从 Review Result 明确保存陈述到 Truth Inbox。",
        changedFields: ["truthInboxStatus", "reviewDueAt"],
        now,
      }),
    );
    return updated;
  }
  const existingDerived = (
    await database.structuredRecords.where("recordType").equals("evidence_statement").toArray()
  ).find(
    (record): record is EvidenceStatement =>
      isEvidenceStatement(record) &&
      record.statementType === targetType &&
      record.supersedesStatementId === source.id &&
      record.sourceRecordIds.includes(review.id),
  );
  if (existingDerived) return existingDerived;
  const derived = createEvidenceStatement(
    {
      type: targetType,
      content: source.content,
      effectiveDate: review.reviewDate,
      generatedBy: "user",
      sourceRecordIds: [...new Set([...source.sourceRecordIds, source.id, review.id])],
      sensitivity: source.sensitivity,
      truthInboxStatus: "inbox",
      reviewDueAt: shiftIsoDays(now, 30),
      supersedesStatementId: source.id,
    },
    now,
  );
  await saveStructuredRecord(database, derived);
  return derived;
}

export async function updateTruthStatement(
  database: CEOOSDatabase,
  statement: EvidenceStatement,
  now = new Date().toISOString(),
): Promise<EvidenceStatement> {
  const updated = { ...statement, updatedAt: now };
  await saveStructuredRecord(
    database,
    updated,
    createAuditEvent({
      entityType: "evidence_statement",
      entityId: updated.id,
      action: "update",
      actor: "user",
      reason: "用户修改 Truth Audit 陈述、证据关系或复核状态。",
      changedFields: [
        "content",
        "statementType",
        "evidenceRefs",
        "supportsStatementIds",
        "contradictsStatementIds",
        "verificationCondition",
        "falsificationCondition",
        "status",
      ],
      now,
    }),
  );
  return updated;
}

export async function captureIdea(
  database: CEOOSDatabase,
  input: {
    title: string;
    description?: string;
    effectiveDate: string;
    coolingHours: number;
    captureSource?: IdeaParkingItem["captureSource"];
    sourceRecordIds?: string[];
    relatedMainline?: string;
    relatedConstraint?: string;
  },
  now = new Date().toISOString(),
): Promise<IdeaParkingItem> {
  if (input.captureSource === "daily_review" && input.sourceRecordIds?.length) {
    const existing = (
      await database.structuredRecords.where("recordType").equals("idea_parking").toArray()
    ).find(
      (record): record is IdeaParkingItem =>
        isIdea(record) &&
        record.captureSource === "daily_review" &&
        Boolean(
          input.sourceRecordIds?.some((sourceId) => record.sourceRecordIds.includes(sourceId)),
        ),
    );
    if (existing) return existing;
  }
  const idea = createIdeaParkingItem(
    input.title.trim(),
    input.effectiveDate,
    coolingUntilFromHours(now, input.coolingHours),
    now,
    {
      ...(input.description === undefined ? {} : { description: input.description.trim() }),
      captureSource: input.captureSource ?? "manual",
      sourceRecordIds: input.sourceRecordIds ?? [],
      relatedMainline: input.relatedMainline ?? "",
      relatedConstraint: input.relatedConstraint ?? "",
    },
  );
  await saveStructuredRecord(database, idea);
  return idea;
}

export async function saveFocusDeviationFromReview(
  database: CEOOSDatabase,
  review: DailyCEOReview,
  correction: string,
  now = new Date().toISOString(),
): Promise<FocusDeviationRecord> {
  const content = review.exportFieldSummaries?.focus_deviation?.trim();
  if (!content) throw new Error("CEO-FOCUS-101: 本次复盘没有可保存的聚焦偏差。");
  const existing = (
    await database.structuredRecords.where("recordType").equals("focus_deviation").toArray()
  ).find(
    (record): record is FocusDeviationRecord =>
      isFocusDeviation(record) && record.sourceReviewId === review.id,
  );
  if (existing) return existing;
  const deviation = createFocusDeviation(
    {
      effectiveDate: review.reviewDate,
      deviationContent: content,
      relatedToNewIdea: /想法|项目|工具|系统/.test(content),
      affectedCriticalBusinessAction: true,
      repeated: false,
      tomorrowCorrection: correction,
      sourceReviewId: review.id,
    },
    now,
  );
  await saveStructuredRecord(database, deviation);
  return deviation;
}

export async function evaluateAndSaveFocusActivation(
  database: CEOOSDatabase,
  ideaId: string,
  answers: FocusActivationAnswers,
  now = new Date().toISOString(),
): Promise<{ idea: IdeaParkingItem; decision: FocusActivationDecision }> {
  const existing = await database.structuredRecords.get(ideaId);
  if (!existing || !isIdea(existing)) throw new Error("CEO-FOCUS-102: 找不到停放想法。");
  const decision = evaluateFocusActivation(existing, answers, now);
  const parkingStatus: IdeaParkingItem["parkingStatus"] =
    decision.result === "reject"
      ? "rejected"
      : decision.result === "escalate_to_board"
        ? "escalated_to_board"
        : decision.result === "small_reversible_experiment"
          ? "under_review"
          : "parked";
  const idea: IdeaParkingItem = {
    ...existing,
    parkingStatus,
    ...(decision.result === "remain_parked"
      ? {
          activationBlockReason:
            decision.issues.find((item) => item.severity === "blocking")?.message ??
            "审查未通过，保持停放。",
        }
      : {}),
    activationReview: {
      ...answers,
      result: decision.result,
      reviewedAt: now,
    },
    updatedAt: now,
  };
  const audit = createAuditEvent({
    entityType: "idea_parking",
    entityId: idea.id,
    action: "update",
    actor: "user",
    reason: `用户完成 FocusActivationGate；结果为 ${decision.result}。`,
    changedFields: ["parkingStatus", "activationReview", "activationBlockReason"],
    now,
  });
  await saveStructuredRecord(database, idea, audit);

  if (decision.result === "escalate_to_board") {
    const escalation: FocusEscalationRecord = {
      ...createEntityMetadata(
        { kind: "derived", referenceIds: [idea.id] },
        { now, status: "draft", exportAllowed: false },
      ),
      recordType: "focus_escalation",
      effectiveDate: idea.effectiveDate,
      sourceRecordIds: [idea.id],
      userConfirmation: "unconfirmed",
      ideaId: idea.id,
      escalationStatus: "draft",
      summary: {
        hasMainlineConflict: answers.servesMainline !== true,
        hasNewProjectRequest: true,
        resourceConflictSummary: answers.resourceRequirement,
        boardReviewRequest: "请董事会 OS 复核该新项目请求与资源冲突。",
      },
    };
    await saveStructuredRecord(database, escalation);
  }
  return { idea, decision };
}

export async function addManualNoStart(
  database: CEOOSDatabase,
  input: {
    title: string;
    reason: string;
    effectiveDate: string;
    activeUntil?: string;
    reviewDueAt?: string;
  },
  now = new Date().toISOString(),
): Promise<NoStartItem> {
  const item = createNoStartItem(
    {
      title: input.title.trim(),
      reason: input.reason.trim(),
      effectiveDate: input.effectiveDate,
      origin: "manual_entry",
      ...(input.activeUntil === undefined ? {} : { activeUntil: input.activeUntil }),
      ...(input.reviewDueAt === undefined ? {} : { reviewDueAt: input.reviewDueAt }),
    },
    now,
  );
  await saveStructuredRecord(database, item);
  return item;
}

export async function acknowledgeBoardNoStart(
  database: CEOOSDatabase,
  context: BusinessContextView,
  title: string,
  dateKey: string,
  requestReview: boolean,
  now = new Date().toISOString(),
): Promise<NoStartItem> {
  const existing = (
    await database.structuredRecords.where("recordType").equals("no_start").toArray()
  ).find(
    (record): record is NoStartItem =>
      isNoStart(record) &&
      record.origin === "board_import" &&
      record.sourceContextId === context.sourceId &&
      record.title === title,
  );
  const item =
    existing ??
    createNoStartItem(
      {
        title,
        reason: "来自只读 BoardContextImport。",
        effectiveDate: dateKey,
        origin: "board_import",
        sourceContextId: context.sourceId,
      },
      now,
    );
  const updated: NoStartItem = {
    ...item,
    readAt: item.readAt ?? now,
    ...(requestReview ? { reviewRequestedAt: now } : {}),
    updatedAt: now,
  };
  await saveStructuredRecord(
    database,
    updated,
    existing
      ? createAuditEvent({
          entityType: "no_start",
          entityId: updated.id,
          action: "update",
          actor: "user",
          reason: requestReview
            ? "用户对董事会 No-Start 项提出复核请求；未删除或改写原项。"
            : "用户标记已读董事会 No-Start 项；未删除或改写原项。",
          changedFields: requestReview ? ["readAt", "reviewRequestedAt"] : ["readAt"],
          now,
        })
      : undefined,
  );
  return updated;
}

export async function createPromise(
  database: CEOOSDatabase,
  input: Parameters<typeof createPromiseEntry>[0],
  now = new Date().toISOString(),
): Promise<PromiseEntry> {
  if (input.promiseSource === "daily_review" && input.sourceRecordIds?.length) {
    const existing = (
      await database.structuredRecords.where("recordType").equals("promise").toArray()
    ).find(
      (record): record is PromiseEntry =>
        isPromise(record) &&
        record.promiseSource === "daily_review" &&
        Boolean(
          input.sourceRecordIds?.some((sourceId) => record.sourceRecordIds.includes(sourceId)),
        ),
    );
    if (existing) return existing;
  }
  const promise = createPromiseEntry(input, now);
  await saveStructuredRecord(database, promise);
  return promise;
}

export type PromiseAction =
  | { type: "request_renegotiation"; note: string }
  | { type: "renegotiate"; nextDueAt: string; note: string }
  | { type: "fulfill"; evidence: string }
  | { type: "cancel"; reason: string }
  | { type: "breach"; reason: string };

export async function applyPromiseAction(
  database: CEOOSDatabase,
  promiseId: string,
  action: PromiseAction,
  now = new Date().toISOString(),
): Promise<{ promise: PromiseEntry; issues: PromiseRuleIssue[] }> {
  const existing = await database.structuredRecords.get(promiseId);
  if (!existing || !isPromise(existing)) throw new Error("CEO-PROMISE-101: 找不到承诺。");
  const result =
    action.type === "request_renegotiation"
      ? requestPromiseRenegotiation(existing, action.note, now)
      : action.type === "renegotiate"
        ? renegotiatePromise(existing, action.nextDueAt, action.note, now)
        : action.type === "fulfill"
          ? fulfillPromise(existing, action.evidence, now)
          : action.type === "cancel"
            ? cancelPromise(existing, action.reason, now)
            : markPromiseBreached(existing, action.reason, now);
  if (result.issues.some((item) => item.severity === "blocking")) {
    return result;
  }
  await saveStructuredRecord(
    database,
    result.promise,
    createAuditEvent({
      entityType: "promise",
      entityId: result.promise.id,
      action: "update",
      actor: "user",
      reason: `用户执行承诺状态动作：${action.type}。`,
      changedFields: [
        "status",
        "promiseStatus",
        "dueAt",
        "renegotiationNote",
        "fulfillmentEvidence",
        "cancellationReason",
        "breachReason",
        "history",
      ],
      now,
    }),
  );
  return result;
}

export async function recordGovernanceProjectionPreview(
  database: CEOOSDatabase,
  projection: GovernanceRiskProjection,
  now = new Date().toISOString(),
): Promise<void> {
  await database.auditEvents.put(
    createAuditEvent({
      entityType: "GovernanceRiskProjection",
      entityId: crypto.randomUUID(),
      action: "export",
      actor: "user",
      reason:
        "用户逐字段查看并确认 Round 03 最小风险投影；本动作只记录预览确认，不自动发送外部消息。",
      changedFields: Object.keys(projection),
      now,
    }),
  );
}
